import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  History,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  UserPlus,
  Mail,
  Phone,
  MapPin,
  Bell,
  BellOff,
  Camera,
  PawPrint,
  Edit,
  Globe,
  FileSpreadsheet,
  User,
  X,
  RefreshCw,
  Calendar,
  CalendarX,
  CalendarClock,
  Tag,
  FileText
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

// Custom Dropdown Component
interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({ value, onChange, options, placeholder = 'Select...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);
  const displayLabel = selectedOption?.label || placeholder;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover focus:outline-none focus:ring-2 focus:ring-gray-400 min-w-[140px]"
      >
        <span className="truncate">{displayLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[160px] bg-white dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg py-1">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors ${
                value === option.value 
                  ? 'font-medium text-gray-900 dark:text-dark-text-primary bg-gray-50 dark:bg-dark-bg-hover' 
                  : 'text-gray-700 dark:text-dark-text-secondary'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Types
interface AuditLogOwner {
  id: string;
  full_name: string | null;
}

interface AuditLogEntry {
  id: string;
  pet_owner_id: string;
  event_type: string;
  actor_id: string | null;
  actor_name: string | null;
  old_value: Record<string, any> | null;
  new_value: Record<string, any> | null;
  event_metadata: Record<string, any> | null;
  description: string | null;
  created_at: string;
  owner?: AuditLogOwner | null;
}

interface AuditLogListResponse {
  audit_logs: AuditLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

interface PetOwnerAuditLogProps {
  ownerId?: string;  // If provided, filter to single owner
  showOwnerColumn?: boolean;  // Whether to show the owner name column (for CRM view)
}

const EVENT_TYPE_OPTIONS: DropdownOption[] = [
  { value: '', label: 'All Events' },
  { value: 'pet_owner_created', label: 'Created (Staff)' },
  { value: 'pet_owner_created_booking', label: 'Created (Booking)' },
  { value: 'pet_owner_created_import', label: 'Created (Import)' },
  { value: 'pet_owner_updated', label: 'Updated' },
  { value: 'email_changed', label: 'Email Changed' },
  { value: 'phone_changed', label: 'Phone Changed' },
  { value: 'tag_added', label: 'Tag Added' },
  { value: 'tag_removed', label: 'Tag Removed' },
  { value: 'note_updated', label: 'Note Updated' },
  { value: 'pet_added', label: 'Pet Added' },
  { value: 'notifications_enabled', label: 'Notifications On' },
  { value: 'notifications_disabled', label: 'Notifications Off' },
  { value: 'photo_uploaded', label: 'Photo Uploaded' },
  { value: 'appointment_created', label: 'Appointment (Staff)' },
  { value: 'appointment_created_booking', label: 'Appointment (Booking)' },
  { value: 'appointment_cancelled', label: 'Appointment Cancelled' },
  { value: 'appointment_rescheduled', label: 'Appointment Rescheduled' },
];

const PetOwnerAuditLog: React.FC<PetOwnerAuditLogProps> = ({ ownerId, showOwnerColumn = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  // Get filter values from URL params
  const page = parseInt(searchParams.get('audit_page') || '1', 10);
  const searchQuery = searchParams.get('audit_search') || '';
  const eventTypeFilter = searchParams.get('event_type') || '';
  const sortOrder = (searchParams.get('audit_sort') || 'desc') as 'asc' | 'desc';
  const pageSize = 25;
  
  // Helper to update URL params
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });
      if (!('audit_page' in updates) && Object.keys(updates).length > 0) {
        newParams.delete('audit_page');
      }
      return newParams;
    });
  }, [setSearchParams]);
  
  const setPage = (p: number) => updateParams({ audit_page: p === 1 ? null : p.toString() });
  const setSearchQueryParam = (v: string) => updateParams({ audit_search: v || null });
  const setEventTypeFilter = (v: string) => updateParams({ event_type: v || null });
  const setSortOrder = (v: 'asc' | 'desc') => updateParams({ audit_sort: v === 'desc' ? null : v });

  useEffect(() => {
    if (eventTypeFilter) {
      setShowFilters(true);
    }
  }, [eventTypeFilter]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const offset = (page - 1) * pageSize;
      
      // Build URL params
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString(),
        sort_order: sortOrder,
      });
      
      if (eventTypeFilter) {
        params.append('event_type', eventTypeFilter);
      }
      
      let url: string;
      if (ownerId) {
        url = `${API_ENDPOINTS.PET_OWNER_AUDIT.LIST(ownerId)}?${params}`;
      } else {
        // CRM-level: fetch all audit logs
        url = `${API_ENDPOINTS.PET_OWNER_AUDIT.LIST_ALL}?${params}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data: AuditLogListResponse = await response.json();
        setLogs(data.audit_logs);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [ownerId, page, eventTypeFilter, sortOrder]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchLogs();
  };

  const clearFilters = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('audit_page');
      newParams.delete('audit_search');
      newParams.delete('event_type');
      newParams.delete('audit_sort');
      return newParams;
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'pet_owner_created':
        return <UserPlus className="w-4 h-4" />;
      case 'pet_owner_created_booking':
        return <Globe className="w-4 h-4" />;
      case 'pet_owner_created_import':
        return <FileSpreadsheet className="w-4 h-4" />;
      case 'pet_owner_updated':
        return <Edit className="w-4 h-4" />;
      case 'email_changed':
        return <Mail className="w-4 h-4" />;
      case 'phone_changed':
        return <Phone className="w-4 h-4" />;
      case 'address_changed':
        return <MapPin className="w-4 h-4" />;
      case 'tag_added':
      case 'tag_removed':
        return <Tag className="w-4 h-4" />;
      case 'note_updated':
        return <FileText className="w-4 h-4" />;
      case 'pet_added':
      case 'pet_removed':
        return <PawPrint className="w-4 h-4" />;
      case 'photo_uploaded':
        return <Camera className="w-4 h-4" />;
      case 'notifications_enabled':
        return <Bell className="w-4 h-4" />;
      case 'notifications_disabled':
        return <BellOff className="w-4 h-4" />;
      case 'appointment_created':
      case 'appointment_created_booking':
        return <Calendar className="w-4 h-4" />;
      case 'appointment_cancelled':
        return <CalendarX className="w-4 h-4" />;
      case 'appointment_rescheduled':
        return <CalendarClock className="w-4 h-4" />;
      default:
        return <History className="w-4 h-4" />;
    }
  };

  const getEventBadgeStyle = (eventType: string) => {
    // Appointment events
    if (eventType === 'appointment_cancelled') {
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    }
    if (eventType === 'appointment_rescheduled') {
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
    if (eventType.includes('appointment_created')) {
      return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400';
    }
    // Pet owner profile events
    if (eventType.includes('created')) {
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    }
    if (eventType.includes('updated') || eventType.includes('_changed')) {
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    }
    if (eventType.includes('pet_added') || eventType.includes('pet_removed')) {
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    }
    if (eventType.includes('notifications_enabled')) {
      return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400';
    }
    if (eventType.includes('notifications_disabled')) {
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    }
    if (eventType.includes('photo')) {
      return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
    }
    if (eventType.includes('tag_')) {
      return 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400';
    }
    if (eventType === 'note_updated') {
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
    }
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  const formatEventType = (eventType: string) => {
    return eventType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
        {/* Search and Filters */}
        <div className="flex items-center gap-3">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by description..."
              value={searchQuery}
              onChange={(e) => setSearchQueryParam(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </form>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
              showFilters || eventTypeFilter
                ? 'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-dark-border text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>

          <button
            onClick={fetchLogs}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700 dark:text-dark-text-secondary">Filters</span>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-dark-text-muted dark:hover:text-dark-text-secondary"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-3">
              <CustomDropdown
                value={eventTypeFilter}
                onChange={setEventTypeFilter}
                options={EVENT_TYPE_OPTIONS}
                placeholder="Event Type"
              />
              <CustomDropdown
                value={sortOrder}
                onChange={(v) => setSortOrder(v as 'asc' | 'desc')}
                options={[
                  { value: 'desc', label: 'Newest First' },
                  { value: 'asc', label: 'Oldest First' },
                ]}
                placeholder="Sort Order"
              />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
            <History className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">No activity recorded</p>
            <p className="text-sm">Changes to pet owners will appear here</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Event
                </th>
                {showOwnerColumn && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                    Client
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  By
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider cursor-pointer hover:text-gray-700"
                    onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}>
                  Date
                  {sortOrder === 'desc' ? <ChevronDown className="w-3 h-3 ml-1 inline" /> : <ChevronUp className="w-3 h-3 ml-1 inline" />}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getEventBadgeStyle(log.event_type)}`}>
                      {getEventIcon(log.event_type)}
                      {formatEventType(log.event_type)}
                    </span>
                  </td>
                  {showOwnerColumn && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      {log.owner?.full_name ? (
                        <Link 
                          to={`/dashboard/pet_owners/${log.pet_owner_id}`}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {log.owner.full_name}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 dark:text-dark-text-primary max-w-md truncate" title={log.description || ''}>
                      {log.description || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {log.actor_name ? (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                          <User className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                        </div>
                        <span className="text-sm text-gray-700 dark:text-dark-text-secondary">
                          {log.actor_name}
                        </span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">System</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-600 dark:text-dark-text-secondary">
                      {formatDate(log.created_at)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm text-gray-700 dark:text-dark-text-secondary">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetOwnerAuditLog;
